
import { useEffect, useRef, useCallback } from "react";
import { supabase, registerChannel } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

interface GroupRealtimeSubscriptionsProps {
  groupId: string;
  fetchMembers: () => Promise<void>;
  fetchExpenses: () => Promise<void>;
  fetchSettlements: () => Promise<void>;
}

export const GroupRealtimeSubscriptions = ({
  groupId,
  fetchMembers,
  fetchExpenses,
  fetchSettlements
}: GroupRealtimeSubscriptionsProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const lastFetchTimes = useRef<Record<string, number>>({
    members: 0,
    expenses: 0,
    settlements: 0
  });
  const debounceTimers = useRef<Record<string, NodeJS.Timeout | null>>({
    members: null,
    expenses: null,
    settlements: null
  });
  const isSubscribedRef = useRef(false);

  // Implement immediate member update for removal events
  const handleMemberChange = useCallback((payload: any) => {
    console.log(`Member change detected: ${payload.eventType}`, payload);
    
    // For removals, fetch immediately without throttling
    if (payload.eventType === 'DELETE') {
      console.log('Member removed, updating immediately');
      fetchMembers().catch(err => console.error('Error fetching members after removal:', err));
    } else {
      // For other changes, use throttled fetch
      throttledFetch('members', fetchMembers);
    }
  }, [fetchMembers]);

  // Throttle delay to prevent excessive updates
  const throttledFetch = useCallback((type: 'members' | 'expenses' | 'settlements', fetchFn: () => Promise<void>) => {
    const now = Date.now();
    // Reasonable throttle delay to reduce stuttering but ensure data is fresh
    const THROTTLE_DELAY = 3000; // 3 seconds between fetch calls
    
    if (debounceTimers.current[type]) {
      clearTimeout(debounceTimers.current[type]!);
      debounceTimers.current[type] = null;
    }
    
    if (now - lastFetchTimes.current[type] < THROTTLE_DELAY) {
      debounceTimers.current[type] = setTimeout(() => {
        lastFetchTimes.current[type] = Date.now();
        fetchFn().catch(err => console.error(`Error fetching ${type}:`, err));
      }, THROTTLE_DELAY);
    } else {
      lastFetchTimes.current[type] = now;
      fetchFn().catch(err => console.error(`Error fetching ${type}:`, err));
    }
  }, []);

  useEffect(() => {
    if (!groupId || isSubscribedRef.current) return;
    
    console.log(`Setting up real-time subscriptions for groupId: ${groupId}`);
    isSubscribedRef.current = true;
    
    const setupSubscription = (channelName: string, table: string, filter: any, callback: (payload: any) => void) => {
      const channel = registerChannel(
        channelName,
        supabase.channel(channelName)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: table,
            filter: filter
          }, (payload) => {
            console.log(`Change detected in ${table}:`, payload.eventType);
            if (typeof callback === 'function') {
              try {
                callback(payload);
              } catch (error) {
                console.error(`Error handling ${table} change:`, error);
              }
            }
          })
          .subscribe((status) => {
            console.log(`${channelName} subscription status: ${status}`);
          })
      );
      
      return channel;
    };
    
    // Set up group details subscription (only respond to archive events)
    setupSubscription(
      `group-details-${groupId}`,
      'groups',
      `id=eq.${groupId}`,
      (payload) => {
        if (payload?.new && payload?.old && 
            payload.new.status === 'archived' && payload.old.status === 'active') {
          toast({
            description: t('groups.archiveGroupInfo'),
          });
          navigate('/app');
        }
      }
    );
    
    // Set up members subscription with special handling for member removals
    setupSubscription(
      `members-${groupId}`,
      'group_members',
      `group_id=eq.${groupId}`,
      handleMemberChange
    );
    
    // Set up expenses subscription
    setupSubscription(
      `expenses-${groupId}`,
      'expenses',
      `group_id=eq.${groupId}`,
      () => throttledFetch('expenses', fetchExpenses)
    );
    
    // Fix the filter for expense_shares to use a simpler approach
    setupSubscription(
      `expense-shares-${groupId}`,
      'expense_shares',
      { foreignTable: 'expenses', foreignColumn: 'id', column: 'expense_id', filter: `group_id=eq.${groupId}` },
      () => throttledFetch('expenses', fetchExpenses)
    );

    // Set up settlements subscription
    setupSubscription(
      `settlements-${groupId}`,
      'settlements',
      `group_id=eq.${groupId}`,
      () => throttledFetch('settlements', fetchSettlements)
    );
    
    return () => {
      console.log('Cleaning up subscriptions...');
      isSubscribedRef.current = false;
      
      // Clean up all timers
      Object.values(debounceTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [groupId, navigate, fetchMembers, fetchExpenses, fetchSettlements, toast, t, throttledFetch, handleMemberChange]);

  return null;
};
